import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useRoles } from "@/context/RolesContext";
import {
  Clock, CaretLeft, CaretRight, FloppyDisk, PaperPlaneTilt, Plus, Trash,
  CheckCircle, XCircle, ArrowClockwise, DownloadSimple, ArrowUUpLeft,
} from "@phosphor-icons/react";

// ---- Date helpers ----
const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const mondayOf = (d) => {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Mon=0, Sun=6
  x.setDate(x.getDate() - dow);
  return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtShort = (d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const fmtRange = (m) => `${m.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${addDays(m,6).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;

const STATUS_STYLES = {
  draft:     { cls: "bg-muted text-foreground", label: "DRAFT" },
  submitted: { cls: "bg-[#F59E0B] text-white",   label: "PENDING" },
  approved:  { cls: "bg-[#22C55E] text-white",   label: "APPROVED" },
  rejected:  { cls: "bg-[#FF3B30] text-white",   label: "REJECTED" },
};

// =============== MY TIMESHEET TAB ===============
function MyTimesheetTab() {
  const [weekMonday, setWeekMonday] = useState(() => mondayOf(new Date()));
  const [week, setWeek] = useState(null);
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]); // [{project_id, days: {isoDate: {hours, description}}}]
  const [dirty, setDirty] = useState(false);
  const [config, setConfig] = useState({ default_approver_role: "manager", max_hours_per_day: 12 });
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const weekIso = toISO(weekMonday);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekMonday, i)), [weekMonday]);

  const flash = (type, text, ms = 3000) => {
    setMsg({ type, text });
    if (ms) setTimeout(() => setMsg({ type: "", text: "" }), ms);
  };

  const projectsById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);

  const rebuildRows = (entries) => {
    // Group entries by project_id; each project row has days keyed by ISO date
    const grouped = {};
    for (const e of (entries || [])) {
      if (!grouped[e.project_id]) grouped[e.project_id] = { project_id: e.project_id, days: {} };
      grouped[e.project_id].days[e.date] = {
        hours: e.hours != null ? String(e.hours) : "",
        description: e.description || "",
        id: e.id,
      };
    }
    return Object.values(grouped);
  };

  const load = useCallback(async () => {
    try {
      const [w, ps, cfg] = await Promise.all([
        api.get(`/timesheets/me?week=${weekIso}`),
        api.get("/projects"),
        api.get("/timesheets/config"),
      ]);
      setWeek(w.data);
      setProjects(ps.data || []);
      setConfig(cfg.data);
      setRows(rebuildRows(w.data.entries));
      setDirty(false);
    } catch (e) {
      flash("error", formatAxiosError(e), 5000);
    }
  }, [weekIso]);

  useEffect(() => { load(); }, [load]);

  const readOnly = week && (week.status === "submitted" || week.status === "approved");

  const dailyTotals = useMemo(() => {
    const t = {};
    days.forEach(d => { t[toISO(d)] = 0; });
    rows.forEach(r => {
      Object.entries(r.days).forEach(([iso, cell]) => {
        const h = parseFloat(cell.hours || 0);
        if (!isNaN(h)) t[iso] = (t[iso] || 0) + h;
      });
    });
    return t;
  }, [rows, days]);

  const weekTotal = useMemo(() => Object.values(dailyTotals).reduce((a, b) => a + b, 0), [dailyTotals]);

  const setCell = (rowIdx, iso, patch) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[rowIdx] };
      row.days = { ...row.days };
      row.days[iso] = { ...(row.days[iso] || { hours: "", description: "" }), ...patch };
      // Remove empty cell
      if (!row.days[iso].hours && !row.days[iso].description) {
        delete row.days[iso];
      }
      next[rowIdx] = row;
      return next;
    });
    setDirty(true);
  };

  const setProjectForRow = (rowIdx, pid) => {
    setRows(prev => {
      const next = [...prev];
      next[rowIdx] = { ...next[rowIdx], project_id: pid };
      return next;
    });
    setDirty(true);
  };

  const addRow = () => {
    setRows(prev => [...prev, { project_id: "", days: {} }]);
    setDirty(true);
  };

  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const gatherEntries = () => {
    const out = [];
    for (const r of rows) {
      if (!r.project_id) continue;
      for (const [iso, cell] of Object.entries(r.days)) {
        const h = parseFloat(cell.hours || 0);
        if (isNaN(h) || h <= 0) continue;
        out.push({ id: cell.id, date: iso, project_id: r.project_id, hours: h, description: cell.description || "" });
      }
    }
    return out;
  };

  const save = async () => {
    setSaving(true);
    try {
      const entries = gatherEntries();
      const r = await api.put(`/timesheets/me?week=${weekIso}`, { entries });
      setWeek(r.data);
      setRows(rebuildRows(r.data.entries));
      setDirty(false);
      flash("ok", "Saved.");
    } catch (e) {
      flash("error", formatAxiosError(e), 6000);
    } finally { setSaving(false); }
  };

  const submit = async () => {
    if (dirty) { await save(); }
    setSubmitting(true);
    try {
      const r = await api.post(`/timesheets/${week.id}/submit`);
      setWeek(r.data);
      flash("ok", "Submitted for approval.");
    } catch (e) {
      flash("error", formatAxiosError(e), 6000);
    } finally { setSubmitting(false); }
  };

  const withdraw = async () => {
    try {
      const r = await api.post(`/timesheets/${week.id}/withdraw`);
      setWeek(r.data);
      flash("ok", "Withdrawn — back to draft.");
    } catch (e) {
      flash("error", formatAxiosError(e), 6000);
    }
  };

  if (!week) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const statusStyle = STATUS_STYLES[week.status] || STATUS_STYLES.draft;

  // Rows used for rendering: existing + a placeholder empty row if none / adding
  const projectOptions = projects.filter(p => p.status !== "completed");
  const availableProjectsForRow = (currentPid, otherPids) => projectOptions.filter(p => p.id === currentPid || !otherPids.has(p.id));

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekMonday(addDays(weekMonday, -7))} className="p-2 border border-border hover:bg-muted" data-testid="prev-week">
            <CaretLeft size={16} weight="bold" />
          </button>
          <div className="min-w-[220px] text-center border border-border px-3 py-2">
            <div className="eyebrow text-[10px]">Week of</div>
            <div className="font-display font-bold" data-testid="week-range">{fmtRange(weekMonday)}</div>
          </div>
          <button onClick={() => setWeekMonday(addDays(weekMonday, 7))} className="p-2 border border-border hover:bg-muted" data-testid="next-week">
            <CaretRight size={16} weight="bold" />
          </button>
          <button onClick={() => setWeekMonday(mondayOf(new Date()))} className="text-xs font-mono-mini uppercase px-3 py-2 border border-border hover:bg-muted" data-testid="this-week">
            Today
          </button>
          <span className={`ml-2 text-[10px] px-2 py-1 uppercase font-mono-mini ${statusStyle.cls}`} data-testid="week-status">
            {statusStyle.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {week.status === "submitted" && (
            <button onClick={withdraw} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="withdraw-btn">
              <ArrowUUpLeft size={14} weight="bold" /> Withdraw
            </button>
          )}
          {!readOnly && (
            <>
              <button onClick={save} disabled={saving || !dirty} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 disabled:opacity-40" data-testid="save-btn">
                <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={submit} disabled={submitting || rows.length === 0} className="btn-primary flex items-center gap-2 disabled:opacity-40" data-testid="submit-btn">
                <PaperPlaneTilt size={14} weight="bold" /> {submitting ? "Submitting…" : "Submit"}
              </button>
            </>
          )}
        </div>
      </div>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`} data-testid="ts-flash">
          {msg.text}
        </div>
      )}

      {week.status === "rejected" && (week.approvals || []).some(a => a.status === "rejected") && (
        <div className="mb-4 border-l-4 border-[#FF3B30] bg-red-50 px-4 py-3">
          <div className="eyebrow mb-1 text-[#FF3B30]">Rejection notes</div>
          {(week.approvals || []).filter(a => a.status === "rejected").map(a => (
            <div key={a.project_id} className="text-sm">
              <b>{projectsById[a.project_id]?.name || a.project_id}:</b> {a.rejected_reason || "No reason given."}
            </div>
          ))}
          <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">Edit your entries and resubmit.</div>
        </div>
      )}

      {/* Grid */}
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-3 py-3 text-left eyebrow min-w-[200px]">Project</th>
              {days.map(d => {
                const isToday = toISO(d) === toISO(new Date());
                return (
                  <th key={toISO(d)} className={`px-2 py-3 text-center eyebrow ${isToday ? "text-[#002FA7]" : ""}`}>
                    <div>{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                    <div className="text-[10px] text-muted-foreground">{d.getDate()}</div>
                  </th>
                );
              })}
              <th className="px-3 py-3 text-right eyebrow">Total</th>
              {!readOnly && <th className="px-2 py-3 w-8"></th>}
            </tr>
          </thead>
          <tbody data-testid="timesheet-grid">
            {rows.length === 0 && !readOnly && (
              <tr>
                <td colSpan={9} className="p-6 text-center text-sm text-muted-foreground">
                  No entries yet. Click <b>Add row</b> below to start logging hours.
                </td>
              </tr>
            )}
            {rows.map((row, idx) => {
              const otherPids = new Set(rows.filter((_, i) => i !== idx).map(r => r.project_id).filter(Boolean));
              const options = availableProjectsForRow(row.project_id, otherPids);
              const rowTotal = Object.values(row.days).reduce((sum, c) => {
                const h = parseFloat(c.hours || 0); return sum + (isNaN(h) ? 0 : h);
              }, 0);
              return (
                <tr key={idx} className="border-t border-border" data-testid={`ts-row-${idx}`}>
                  <td className="px-3 py-2">
                    {readOnly ? (
                      <div className="font-semibold text-sm">{projectsById[row.project_id]?.name || "—"}</div>
                    ) : (
                      <select
                        value={row.project_id}
                        onChange={(e) => setProjectForRow(idx, e.target.value)}
                        className="w-full border border-border px-2 py-1.5 text-sm bg-white"
                        data-testid={`ts-project-${idx}`}
                      >
                        <option value="">Select project…</option>
                        {options.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  {days.map(d => {
                    const iso = toISO(d);
                    const cell = row.days[iso] || { hours: "", description: "" };
                    return (
                      <td key={iso} className="px-1 py-2 text-center align-top">
                        {readOnly ? (
                          <div className="text-center">
                            <div className="font-mono-mini text-sm">{cell.hours || "—"}</div>
                            {cell.description && <div className="text-[10px] text-muted-foreground truncate max-w-[80px] mx-auto" title={cell.description}>{cell.description}</div>}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <input
                              type="number"
                              step="0.25"
                              min="0"
                              max="24"
                              value={cell.hours}
                              onChange={(e) => setCell(idx, iso, { hours: e.target.value })}
                              className="w-full border border-border px-1 py-1 text-sm text-center font-mono-mini focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                              placeholder="0"
                              data-testid={`ts-hours-${idx}-${iso}`}
                              disabled={!row.project_id}
                            />
                            <input
                              type="text"
                              value={cell.description}
                              onChange={(e) => setCell(idx, iso, { description: e.target.value })}
                              className="w-full border border-border px-1 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                              placeholder="Notes"
                              data-testid={`ts-desc-${idx}-${iso}`}
                              disabled={!row.project_id}
                            />
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-mono-mini font-bold" data-testid={`ts-row-total-${idx}`}>{rowTotal.toFixed(2)}</td>
                  {!readOnly && (
                    <td className="px-1 py-2 text-center">
                      <button onClick={() => removeRow(idx)} className="p-1 text-muted-foreground hover:text-[#FF3B30]" data-testid={`ts-delete-row-${idx}`}>
                        <Trash size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-muted">
            <tr>
              <td className="px-3 py-2 eyebrow">Daily total</td>
              {days.map(d => {
                const iso = toISO(d);
                const tot = dailyTotals[iso] || 0;
                const over = tot > config.max_hours_per_day + 0.001;
                return (
                  <td key={iso} className={`px-1 py-2 text-center font-mono-mini text-sm ${over ? "text-[#FF3B30] font-bold" : ""}`} data-testid={`ts-daily-total-${iso}`}>
                    {tot ? tot.toFixed(2) : "—"}
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right font-mono-mini font-bold" data-testid="ts-week-total">{weekTotal.toFixed(2)}</td>
              {!readOnly && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>

      {!readOnly && (
        <button onClick={addRow} className="mt-3 w-full border border-dashed border-border py-2 text-sm hover:bg-muted flex items-center justify-center gap-2" data-testid="add-row-btn">
          <Plus size={14} weight="bold" /> Add row
        </button>
      )}

      {/* Approvals summary */}
      {(week.approvals || []).length > 0 && (
        <div className="mt-6 border border-border">
          <div className="px-4 py-2 bg-muted eyebrow">Approval status</div>
          <div>
            {(week.approvals || []).map(a => {
              const p = projectsById[a.project_id];
              const st = STATUS_STYLES[a.status] || { cls: "bg-muted", label: (a.status || "").toUpperCase() };
              const hours = (week.entries || []).filter(e => e.project_id === a.project_id).reduce((s, e) => s + Number(e.hours || 0), 0);
              return (
                <div key={a.project_id} className="flex items-center gap-3 px-4 py-3 border-t border-border first:border-t-0" data-testid={`ts-approval-${a.project_id}`}>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{p?.name || a.project_id}</div>
                    <div className="text-[10px] font-mono-mini text-muted-foreground">{hours.toFixed(2)} hours logged</div>
                    {a.rejected_reason && <div className="text-xs text-[#FF3B30] mt-1">Reason: {a.rejected_reason}</div>}
                  </div>
                  <span className={`inline-block text-[10px] px-2 py-0.5 uppercase font-mono-mini ${st.cls}`}>{st.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// =============== APPROVALS TAB ===============
function ApprovalsTab() {
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [reportUser, setReportUser] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [rejectContext, setRejectContext] = useState(null); // {tid, project_id}
  const [rejectReason, setRejectReason] = useState("");

  const flash = (type, text, ms = 3000) => {
    setMsg({ type, text });
    if (ms) setTimeout(() => setMsg({ type: "", text: "" }), ms);
  };

  const load = useCallback(async () => {
    try {
      const [p, u] = await Promise.all([
        api.get("/timesheets/pending-for-me"),
        api.get("/users"),
      ]);
      setPending(p.data || []);
      setUsers(u.data || []);
    } catch (e) {
      flash("error", formatAxiosError(e), 5000);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (tid, project_id) => {
    try {
      await api.post(`/timesheets/${tid}/approve`, { project_id });
      flash("ok", "Approved.");
      load();
    } catch (e) {
      flash("error", formatAxiosError(e), 6000);
    }
  };

  const doReject = async () => {
    const { tid, project_id } = rejectContext;
    try {
      await api.post(`/timesheets/${tid}/reject`, { project_id, reason: rejectReason });
      setRejectContext(null); setRejectReason("");
      flash("ok", "Rejected.");
      load();
    } catch (e) {
      flash("error", formatAxiosError(e), 6000);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-1">Queue</div>
          <div className="text-sm text-muted-foreground">
            {pending.length === 0 ? "No timesheets await your approval." : `${pending.length} timesheet(s) awaiting your approval.`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="refresh-approvals">
            <ArrowClockwise size={14} weight="bold" /> Refresh
          </button>
          <button onClick={() => setReportUser({})} className="btn-primary flex items-center gap-2" data-testid="open-report-modal">
            <DownloadSimple size={14} weight="bold" /> Download report
          </button>
        </div>
      </div>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`} data-testid="ts-approval-flash">
          {msg.text}
        </div>
      )}

      <div className="space-y-4" data-testid="approvals-list">
        {pending.map(w => (
          <div key={w.id} className="border border-border">
            <div className="px-4 py-3 border-b border-border bg-muted flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold">{w.user?.name || "Unknown user"}</div>
                <div className="text-[10px] font-mono-mini text-muted-foreground">
                  Week of {w.week_start} · submitted {w.submitted_at?.substring(0, 10)} · {w.total_hours} hrs total
                </div>
              </div>
              <button
                onClick={() => setReportUser(w.user)}
                className="text-xs font-mono-mini uppercase px-3 py-1.5 border border-border hover:bg-white flex items-center gap-1"
                data-testid={`ts-report-user-${w.user?.id}`}
              >
                <DownloadSimple size={12} weight="bold" /> Report
              </button>
            </div>
            <div>
              {(w.pending_for_me || []).map(pfm => (
                <div key={pfm.project_id} className="px-4 py-3 border-t border-border first:border-t-0 flex items-center gap-3 flex-wrap" data-testid={`ts-pending-${w.id}-${pfm.project_id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold text-sm">{pfm.project_name}</div>
                    <div className="text-[10px] font-mono-mini text-muted-foreground">
                      {pfm.hours} hours logged · approver role: <b>{pfm.approver_role}</b>
                    </div>
                    <div className="mt-2 text-xs">
                      {(w.entries || []).filter(e => e.project_id === pfm.project_id).map(e => (
                        <div key={e.id} className="flex items-baseline gap-2 border-l-2 border-border pl-2 mt-1">
                          <span className="font-mono-mini text-[10px] text-muted-foreground w-20">{e.date}</span>
                          <span className="font-mono-mini font-bold w-12">{e.hours}h</span>
                          <span className="text-muted-foreground">{e.description || <em>No description</em>}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approve(w.id, pfm.project_id)}
                      className="border border-[#22C55E] text-[#166534] px-3 py-2 text-sm hover:bg-green-50 flex items-center gap-1"
                      data-testid={`approve-${w.id}-${pfm.project_id}`}
                    >
                      <CheckCircle size={14} weight="bold" /> Approve
                    </button>
                    <button
                      onClick={() => { setRejectContext({ tid: w.id, project_id: pfm.project_id }); setRejectReason(""); }}
                      className="border border-[#FF3B30] text-[#FF3B30] px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-1"
                      data-testid={`reject-${w.id}-${pfm.project_id}`}
                    >
                      <XCircle size={14} weight="bold" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {rejectContext && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRejectContext(null)}>
          <div className="bg-white border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="reject-modal">
            <div className="px-6 py-4 border-b border-border">
              <div className="eyebrow">Timesheet</div>
              <h3 className="font-display text-lg font-bold">Reject entry</h3>
            </div>
            <div className="p-6 space-y-3">
              <label className="eyebrow block mb-1">Reason (optional but recommended)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full border border-border px-3 py-2 text-sm"
                placeholder="e.g. Please split into design vs. review hours."
                data-testid="reject-reason-input"
              />
              <div className="text-[10px] font-mono-mini text-muted-foreground">
                Rejecting bounces the entire week back to the user as a draft. They can edit and resubmit.
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setRejectContext(null)} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
                <button onClick={doReject} className="flex-1 bg-[#FF3B30] text-white px-4 py-2 text-sm hover:opacity-90" data-testid="confirm-reject">Reject</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {reportUser && (
        <ReportModal
          users={users}
          initialUser={reportUser?.id ? reportUser : null}
          onClose={() => setReportUser(null)}
        />
      )}
    </div>
  );
}

function ReportModal({ users, initialUser, onClose }) {
  const [userId, setUserId] = useState(initialUser?.id || "");
  const today = new Date();
  const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  const [from, setFrom] = useState(toISO(monthAgo));
  const [to, setTo] = useState(toISO(today));
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const download = async () => {
    if (!userId) { setError("Pick a user."); return; }
    setDownloading(true); setError("");
    try {
      const r = await api.get(`/timesheets/user/${userId}/report.csv?from=${from}&to=${to}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const uName = users.find(u => u.id === userId)?.name || userId;
      a.download = `timesheet_${uName.replace(/\s+/g, "_").toLowerCase()}_${from}_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      setError(formatAxiosError(e));
    } finally { setDownloading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
        <div className="px-6 py-4 border-b border-border">
          <div className="eyebrow">Timesheet</div>
          <h3 className="font-display text-lg font-bold">Download report</h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="eyebrow block mb-2">User</label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="w-full border border-border px-3 py-2 text-sm bg-white"
              data-testid="report-user-select"
            >
              <option value="">Select a user…</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-2">From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full border border-border px-3 py-2 text-sm" data-testid="report-from" />
            </div>
            <div>
              <label className="eyebrow block mb-2">To</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full border border-border px-3 py-2 text-sm" data-testid="report-to" />
            </div>
          </div>
          {error && <div className="text-xs text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
            <button onClick={download} disabled={downloading || !userId} className="flex-1 btn-primary disabled:opacity-50" data-testid="report-download">
              {downloading ? "Downloading…" : "Download CSV"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// =============== ALL TIMESHEETS TAB ===============
function AllTimesheetsTab() {
  const [timesheets, setTimesheets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(async () => {
    try {
      const [ts, u] = await Promise.all([
        api.get("/timesheets/all"),
        api.get("/users"),
      ]);
      setTimesheets(ts.data || []);
      setUsers(u.data || []);
    } catch (e) {
      console.error("Failed to load timesheets:", e);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => { load(); }, [load]);

  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  const filteredTimesheets = useMemo(() => {
    return timesheets.filter(ts => {
      const matchUser = filterUser === "all" || ts.user_id === filterUser;
      const matchStatus = filterStatus === "all" || ts.status === filterStatus;
      return matchUser && matchStatus;
    });
  }, [timesheets, filterUser, filterStatus]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex gap-4">
        <div>
          <label className="text-sm font-medium block mb-1">User</label>
          <select 
            value={filterUser} 
            onChange={(e) => setFilterUser(e.target.value)}
            className="border border-border rounded px-3 py-2 text-sm"
          >
            <option value="all">All Users</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Status</label>
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-border rounded px-3 py-2 text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Timesheets Table */}
      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="border-b border-border">
              <th className="px-4 py-3 text-left font-semibold">User</th>
              <th className="px-4 py-3 text-left font-semibold">Week</th>
              <th className="px-4 py-3 text-left font-semibold">Total Hours</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Submitted</th>
            </tr>
          </thead>
          <tbody>
            {filteredTimesheets.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">
                  No timesheets found
                </td>
              </tr>
            ) : (
              filteredTimesheets.map((ts) => {
                const user = userMap[ts.user_id];
                const totalHours = (ts.entries || []).reduce((sum, e) => sum + (e.hours || 0), 0);
                const st = STATUS_STYLES[ts.status] || STATUS_STYLES.draft;
                
                return (
                  <tr key={ts.id} className="border-b border-border hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {user?.avatar && (
                          <img src={user.avatar} alt="" className="w-6 h-6 rounded-full" />
                        )}
                        <span className="font-medium">{user?.name || "Unknown"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono-mini">
                      {new Date(ts.week_start).toLocaleDateString()} - {new Date(new Date(ts.week_start).setDate(new Date(ts.week_start).getDate() + 6)).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 font-mono-mini font-semibold">{totalHours}h</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-[10px] px-2 py-0.5 uppercase font-mono-mini ${st.cls}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {ts.submitted_at ? new Date(ts.submitted_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============== PAGE ===============
export default function Timesheet() {
  const { user } = useAuth();
  const { can, loaded } = useCapabilities();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const canApprove = can("approve_timesheets");
  const defaultTab = params.get("tab") === "approvals" && canApprove ? "approvals" : 
                     params.get("tab") === "all" && canApprove ? "all" : "mine";
  const [tab, setTab] = useState(defaultTab);

  useEffect(() => {
    if (params.get("tab") === "approvals" && canApprove) setTab("approvals");
    else if (params.get("tab") === "all" && canApprove) setTab("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, canApprove]);

  if (!loaded) return <div className="p-12 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8">
        <div className="eyebrow mb-2 flex items-center gap-2">
          <Clock size={12} weight="bold" /> Time / Timesheet
        </div>
        <h1 className="font-display text-4xl font-black tracking-tight">
          {tab === "mine" ? "My timesheet" : tab === "approvals" ? "Approvals" : "All Timesheets"}
        </h1>
        <div className="mt-6 flex items-center gap-1 border-b border-border -mb-8">
          <button
            onClick={() => { setTab("mine"); navigate("/timesheet"); }}
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px ${tab === "mine" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="ts-tab-mine"
          >
            My timesheet
          </button>
          {canApprove && (
            <>
              <button
                onClick={() => { setTab("approvals"); navigate("/timesheet?tab=approvals"); }}
                className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px ${tab === "approvals" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                data-testid="ts-tab-approvals"
              >
                Approvals
              </button>
              <button
                onClick={() => { setTab("all"); navigate("/timesheet?tab=all"); }}
                className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px ${tab === "all" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                data-testid="ts-tab-all"
              >
                All Timesheets
              </button>
            </>
          )}
        </div>
      </div>
      <div className="p-8 lg:p-12">
        {tab === "mine" ? <MyTimesheetTab /> : tab === "approvals" ? <ApprovalsTab /> : <AllTimesheetsTab />}
      </div>
    </div>
  );
}
